import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { UserPlus, Share2, Gift } from "lucide-react";

const ReferFriend = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const steps = [
		{
			to: "/register",
			icon: <UserPlus className="w-8 h-8" />,
			title: "Register Now",
			delay: 0.3,
		},
		{
			to: "/profile",
			icon: <Share2 className="w-8 h-8" />,
			title: "Share Code",
			delay: 0.4,
		},
		{
			to: "/refer-a-friend",
			icon: <Gift className="w-8 h-8" />,
			title: "Collect Rewards",
			delay: 0.5,
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50 py-12">
			<div className="container mx-auto">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
					{/* Image Section */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={bounceTransition}
						className="relative"
					>
						<motion.img
							src="/images/refer-friend.png"
							alt="Refer a friend"
							className="w-full max-w-xl mx-auto rounded-3xl shadow-xl"
							initial={{ scale: 0.9 }}
							animate={{ scale: 1 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						/>
						<motion.div
							className="absolute -top-20 -left-20 w-60 h-60 bg-primary/10 rounded-full blur-3xl"
							animate={{
								scale: [1, 1.2, 1],
								rotate: [0, 180, 0],
							}}
							transition={{
								duration: 8,
								repeat: Infinity,
								ease: "linear",
							}}
						/>
					</motion.div>

					{/* Content Section */}
					<div className="space-y-16">
						{/* Header */}
						<motion.div
							className="text-center lg:text-left"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={bounceTransition}
						>
							<motion.h1
								className="text-5xl md:text-6xl font-bold mb-6 leading-tight"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ ...bounceTransition, delay: 0.2 }}
							>
								Give 10% Off,{" "}
								<span className="relative">
									<span className="text-primary">Get 10% Off</span>
									<motion.div
										className="absolute -inset-2 -skew-y-3 bg-primary/10 -z-10 rounded-lg"
										initial={{ scale: 0 }}
										animate={{ scale: 1 }}
										transition={{ ...bounceTransition, delay: 0.4 }}
									/>
								</span>
							</motion.h1>
						</motion.div>

						{/* Steps */}
						<motion.div
							className="space-y-8"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						>
							<h2 className="text-2xl text-center lg:text-left">
								Refer a friend and you'll both get rewarded.
							</h2>

							{/* <motion.p
								className="text-2xl text-gray-600 italic"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ ...bounceTransition, delay: 0.3 }}
							>
								Promotion applies following a referral purchase
							</motion.p> */}

							<div className="flex flex-col md:flex-row items-center justify-between gap-6">
								{steps.map((step, index) => (
									<React.Fragment key={index}>
										<motion.div
											className="group"
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ ...bounceTransition, delay: step.delay }}
										>
											<div className="bg-primary hover:bg-secondary transition-colors duration-300 rounded-full h-32 w-32 flex flex-col items-center justify-center gap-2 p-4">
												<Link
													to={step.to}
													className="flex flex-col gap-2 items-center text-white text-center font-bold"
												>
													<div className="text-white">{step.icon}</div>

													<span>{step.title}</span>
												</Link>
											</div>
										</motion.div>

										{index < steps.length - 1 && (
											<motion.div
												className="hidden md:block text-4xl text-gray-400"
												initial={{ opacity: 0, scale: 0 }}
												animate={{ opacity: 1, scale: 1 }}
												transition={{
													...bounceTransition,
													delay: step.delay + 0.1,
												}}
											>
												→
											</motion.div>
										)}
									</React.Fragment>
								))}
							</div>
						</motion.div>

						{/* CTA Section */}
						<motion.div
							className="space-y-6 text-center lg:text-left"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.6 }}
						>
							<Link to="/profile">
								<motion.button
									className="bg-secondary hover:bg-primary text-white text-2xl md:text-3xl font-bold px-12 py-4 rounded-xl shadow-lg transition-all duration-300"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									Go to my Referral Code
								</motion.button>
							</Link>

							<p className="text-2xl italic">
								Not a member?{" "}
								<Link
									to="/register"
									className="text-primary hover:text-secondary underline transition-colors"
								>
									Join now
								</Link>{" "}
								to start saving!
							</p>
						</motion.div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReferFriend;
